<script lang="ts">
  import type { ValidationResult } from '$lib/editor'

  interface Props {
    result: ValidationResult
    onCycle: () => void
  }

  const { result, onCycle }: Props = $props()

  const counts = $derived.by(() => {
    // Count UNIQUE buildings per category. The validator emits one overlap
    // error per shared tile, so two buildings sharing three tiles produces
    // three errors each carrying the same two ids — naively summing
    // `buildingIds.length` would over-count. A building can legitimately
    // appear in multiple categories at once (e.g. overlapping AND outside
    // the unlocked area); the segments report each independently, which
    // matches the player's mental model.
    const overlapIds = new Set<number>()
    const outsideIds = new Set<number>()
    const disconnectedIds = new Set<number>()
    for (const e of result.errors) {
      const target =
        e.code === 'overlap'
          ? overlapIds
          : e.code === 'outside-unlocked-area'
            ? outsideIds
            : e.code === 'disconnected'
              ? disconnectedIds
              : null
      if (target) for (const id of e.buildingIds) target.add(id)
    }
    return {
      overlap: overlapIds.size,
      outside: outsideIds.size,
      discBuildings: disconnectedIds.size,
    }
  })

  const segments = $derived.by(() => {
    const out: string[] = []
    // Player-facing wording: always count BUILDINGS, never internal terms
    // like "clusters". Singular/plural handled per category — "1 building
    // overlaps", "2 buildings overlap" — so the banner reads naturally at
    // any count. Cluster grouping still lives in the [show] cycle order
    // (cluster A's buildings before cluster B's), but the banner doesn't
    // surface that detail.
    if (counts.overlap > 0) {
      out.push(`${counts.overlap} ${counts.overlap === 1 ? 'building overlaps' : 'buildings overlap'}`)
    }
    if (counts.outside > 0) {
      out.push(
        `${counts.outside} ${counts.outside === 1 ? 'building' : 'buildings'} outside unlocked area`,
      )
    }
    if (counts.discBuildings > 0) {
      out.push(
        `${counts.discBuildings} ${counts.discBuildings === 1 ? 'building needs' : 'buildings need'} road access`,
      )
    }
    return out
  })
</script>

{#if !result.ok}
  <!-- Floating toast: centred, content-sized so it never spans into the
       SelectionPanel (left, w-80) or EditorPanel (right, w-80) and leaves
       both panels' tops clickable. Sits above the panels (z-20 vs panels'
       z-10) so it pops without pushing the work surface down. -->
  <div
    class="pointer-events-auto absolute left-1/2 top-2 z-20 flex max-w-[calc(100%-22rem)] -translate-x-1/2 items-center gap-3 rounded-md border border-validation-error/40 bg-validation-error/15 px-4 py-2 text-base text-validation-error shadow-md"
    role="status"
  >
    <span aria-hidden="true">⚠</span>
    <span>{segments.join(' · ')}</span>
    <button
      type="button"
      class="rounded px-2 py-0.5 text-sm underline hover:bg-validation-error/25"
      onclick={onCycle}>show</button
    >
  </div>
{/if}
